import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../utils/api";
import styles from "./FlowDrilldown.module.css";

/**
 * Three-column drawer that walks Deals -> Loans -> Notes. Each column
 * paginates independently so very large datasets stay responsive.
 *
 * URL on backend: GET /dashboard/flow?dealId=&loanId=&page=&limit=
 */
const PAGE_SIZE = 25;

const fetchFlow = async ({ dealId, loanId, page }) => {
  const params = { page, limit: PAGE_SIZE };
  if (dealId) params.dealId = dealId;
  if (loanId) params.loanId = loanId;
  const { data } = await api.get("dashboard/flow", { params });
  return data;
};

const FlowDrilldown = ({ onClose }) => {
  const navigate = useNavigate();
  const [selectedDeal, setSelectedDeal] = useState(null); // {id, name}
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [dealPage, setDealPage] = useState(1);
  const [loanPage, setLoanPage] = useState(1);
  const [notePage, setNotePage] = useState(1);

  const openInNotesPage = () => {
    if (!selectedLoan) return;
    const qp = new URLSearchParams({ loanId: String(selectedLoan.id) });
    if (selectedNote) qp.set("noteId", String(selectedNote.id));
    onClose?.();
    navigate(`/notes?${qp.toString()}`);
  };

  const dealsQ = useQuery({
    queryKey: ["flow", "deals", dealPage],
    queryFn: () => fetchFlow({ page: dealPage }),
    staleTime: 30_000,
  });
  const loansQ = useQuery({
    queryKey: ["flow", "loans", selectedDeal?.id, loanPage],
    queryFn: () => fetchFlow({ dealId: selectedDeal.id, page: loanPage }),
    enabled: !!selectedDeal,
    staleTime: 30_000,
  });
  const notesQ = useQuery({
    queryKey: ["flow", "notes", selectedDeal?.id, selectedLoan?.id, notePage],
    queryFn: () => fetchFlow({ dealId: selectedDeal.id, loanId: selectedLoan.id, page: notePage }),
    enabled: !!selectedDeal && !!selectedLoan,
    staleTime: 30_000,
  });

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="dd-title">
      <div className={styles.drawer}>
        <header className={styles.head}>
          <div>
            <h2 id="dd-title" className={styles.title}>Data drilldown</h2>
            <p className={styles.crumbs}>
              <span className={selectedDeal ? styles.crumbDone : styles.crumbCurrent}
                    onClick={() => { setSelectedDeal(null); setSelectedLoan(null); }}
                    role="button" tabIndex={0}>
                Deals
              </span>
              {selectedDeal && (<>
                <i className="bi bi-chevron-right" />
                <span className={selectedLoan ? styles.crumbDone : styles.crumbCurrent}
                      onClick={() => setSelectedLoan(null)}
                      role="button" tabIndex={0}>
                  {selectedDeal.name}
                </span>
              </>)}
              {selectedLoan && (<>
                <i className="bi bi-chevron-right" />
                <span className={styles.crumbCurrent}>{selectedLoan.name}</span>
              </>)}
            </p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close drilldown">×</button>
        </header>

        <div className={styles.cols}>
          <Column
            title="Deals"
            query={dealsQ}
            page={dealPage}
            onPage={setDealPage}
            selectedId={selectedDeal?.id}
            onSelect={(it) => { setSelectedDeal(it); setSelectedLoan(null); setLoanPage(1); }}
            renderItem={(it) => (
              <>
                <div className={styles.itemMain}>
                  <span className={styles.itemName}>{it.name || `Deal #${it.id}`}</span>
                  {it.cik && <span className="mono" style={{ color: "var(--text-muted)" }}>CIK {it.cik}</span>}
                </div>
                <div className={styles.itemMeta}>
                  <span className={styles.metric}>{it.loansCount}<small>loans</small></span>
                  <span className={styles.metric}>{it.notesCount}<small>notes</small></span>
                </div>
              </>
            )}
            emptyText="No deals."
          />

          <Column
            title="Loans"
            placeholder="Pick a deal to view its loans"
            query={loansQ}
            page={loanPage}
            onPage={setLoanPage}
            disabled={!selectedDeal}
            selectedId={selectedLoan?.id}
            onSelect={(it) => { setSelectedLoan(it); setNotePage(1); }}
            renderItem={(it) => (
              <>
                <div className={styles.itemMain}>
                  <span className={styles.itemName}>{it.name}</span>
                </div>
                <div className={styles.itemMeta}>
                  <span className={styles.metric}>{it.notesCount}<small>notes</small></span>
                </div>
              </>
            )}
            emptyText="No loans in this deal."
          />

          <Column
            title="Notes"
            placeholder="Pick a loan to view its notes"
            query={notesQ}
            page={notePage}
            onPage={setNotePage}
            disabled={!selectedLoan}
            selectedId={selectedNote?.id}
            onSelect={(it) => setSelectedNote(it)}
            footer={
              selectedLoan && (
                <button
                  type="button"
                  className="btn btn-accent"
                  onClick={openInNotesPage}
                  style={{ width: "100%" }}
                >
                  <i className="bi bi-box-arrow-up-right" />{" "}
                  Open {selectedNote ? `note "${selectedNote.name || selectedNote.id}"` : "loan"} in Notes page
                </button>
              )
            }
            renderItem={(it) => (
              <>
                <div className={styles.itemMain}>
                  <span className={styles.itemName}>{it.name || `Note #${it.id}`}</span>
                  {it.holder && (
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{it.holder}</span>
                  )}
                </div>
                <div className={styles.itemMeta}>
                  <span className={`status-pill ${it.status === "burnt" ? "status-burnt" : "status-active"}`}>
                    {it.status}
                  </span>
                  {it.balance != null && (
                    <span className={styles.balance}>${Number(it.balance).toLocaleString()}</span>
                  )}
                </div>
              </>
            )}
            emptyText="No notes in this loan."
          />
        </div>
      </div>
    </div>
  );
};

const Column = ({ title, query, page, onPage, selectedId, onSelect, renderItem, emptyText, placeholder, disabled, footer }) => {
  const data = query?.data;
  const items = data?.items || [];
  const total = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={`${styles.col} ${disabled ? styles.colDisabled : ""}`}>
      <div className={styles.colHead}>
        <h3 className={styles.colTitle}>{title}</h3>
        {!disabled && data && (
          <span className={styles.colCount}>{total.toLocaleString()} total</span>
        )}
      </div>

      <div className={styles.colBody}>
        {disabled ? (
          <div className={styles.placeholder}>
            <i className="bi bi-arrow-left" />
            <span>{placeholder}</span>
          </div>
        ) : query?.isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className={`skeleton ${styles.itemSkel}`} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className={styles.placeholder}>
            <i className="bi bi-inbox" />
            <span>{emptyText}</span>
          </div>
        ) : (
          <ul className={styles.list}>
            {items.map((it) => (
              <li
                key={it.id}
                className={`${styles.item} ${selectedId === it.id ? styles.itemActive : ""} ${onSelect ? styles.itemSelectable : ""}`}
                onClick={onSelect ? () => onSelect(it) : undefined}
                role={onSelect ? "button" : undefined}
                tabIndex={onSelect ? 0 : undefined}
                onKeyDown={onSelect ? (e) => (e.key === "Enter" || e.key === " ") && onSelect(it) : undefined}
              >
                {renderItem(it)}
                {onSelect && <i className={`bi bi-chevron-right ${styles.chev}`} />}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!disabled && totalPages > 1 && (
        <div className={styles.colFoot}>
          <button className="arrow-button" disabled={page === 1} onClick={() => onPage(page - 1)}>&larr;</button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button className="arrow-button" disabled={page === totalPages} onClick={() => onPage(page + 1)}>&rarr;</button>
        </div>
      )}
      {footer && <div className={styles.colFooter}>{footer}</div>}
    </div>
  );
};

export default FlowDrilldown;
